import React from 'react';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  maxBreakpointQuery,
  standardColours,
  brandColours,
  fontSize,
  zIndexLayers,
  visuallyHidden,
  standardTransition,
} from '../styles';
import { Link } from './ui';
import MegaMenu from './MegaMenu';

const StyledNavigation = styled.nav``;

const StyledToggle = styled.button`
  ${maxBreakpointQuery.mlarge`
    position: relative;
    margin: 0;
    padding: 32px 29px;
    height: 100%;
    background-color: ${brandColours.primary};
  `}

  ${maxBreakpointQuery.tsmall`
    padding: 27px 24px;
  `}

  ${minBreakpointQuery.mlarge`
    display: none;
  `}

  span {
    ${maxBreakpointQuery.mlarge`
      display: block;
      margin-top: 7px;
      margin-bottom: 7px;
      height: 2px;
      width: 26px;
      background-color: ${standardColours.white};
      border-radius: 1px;
    `}

    ${maxBreakpointQuery.tsmall`
      margin-top: 5.5px;
      margin-bottom: 5.5px;
      height: 1px;
      width: 20px;
    `}

    &:first-child {
      ${maxBreakpointQuery.mlarge`
        margin-top: 0;
      `}
    }

    &:last-child {
      ${maxBreakpointQuery.mlarge`
        margin-bottom: 0;
      `}
    }

    ${({ isNavActive }) => {
      if (isNavActive) {
        return css`
          ${maxBreakpointQuery.mlarge`
            opacity: 0;
          `}

          &:first-child,
          &:last-child {
            ${maxBreakpointQuery.mlarge`
              opacity: 1;
            `}
          }

          &:first-child {
            ${maxBreakpointQuery.mlarge`
              transform: translateY(9px) rotate(45deg);
            `}

            ${maxBreakpointQuery.tsmall`
              transform: translateY(6.5px) rotate(45deg);
            `}
          }

          &:last-child {
            ${maxBreakpointQuery.mlarge`
              transform: translateY(-9px) rotate(-45deg);
            `}

            ${maxBreakpointQuery.tsmall`
              transform: translateY(-6.5px) rotate(-45deg);
            `}
          }
        `;
      }
    }}
  }
`;

const StyledList = styled.ul`
  ${maxBreakpointQuery.mlarge`
    position: absolute;
    right: 0;
    left: 0;
    overflow: auto;
    z-index: ${zIndexLayers.fourth};
    display: ${({ isNavActive }) => (isNavActive ? 'block' : 'none')};
    height: 100%;
    background-color: ${brandColours.primary}
  `}
`;

// moved up for hover
const StyledLink = styled(Link)`
  position: relative;
  cursor: pointer;

  ${maxBreakpointQuery.mlarge`
    display: block;
    padding: 17px 50px 17px 20px;
    color: ${standardColours.white};
    ${fontSize(20)};
  `}

  ${maxBreakpointQuery.tsmall`
    ${fontSize(18)};
  `}

  ${minBreakpointQuery.mlarge`
    padding-top: 5px;
    padding-bottom: 5px;
    ${fontSize(14)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(15)};
  `}

  ${minBreakpointQuery.xlarge`
    padding-right: 10px;
  `}

  ${minBreakpointQuery.xxlarge`
    padding-right: 14px;
  `}

  ${minBreakpointQuery.xxxlarge`
    ${fontSize(16)};
  `}

  > span {
    position: relative;
  }

  &:before {
    ${minBreakpointQuery.mlarge`
      content: '';
      position: absolute;
      bottom: 5px;
      left: 0;
      height: 1px;
      width: 100%;
      background-color: ${({ alt, altTheme }) =>
        alt || altTheme ? standardColours.white : brandColours.primary};
      opacity: 0;
      transition: ${standardTransition('opacity')};
    `}
  }

  &:after {
    ${maxBreakpointQuery.mlarge`
      content: '➔';
      position: absolute;
      top: 50%;
      right: 20px;
      color: ${standardColours.white};
      ${fontSize(20)};
      transform: translateY(-50%);
    `}

    ${minBreakpointQuery.xlarge`
      content: '';
      position: absolute;
      top: 50%;
      right: 0;
      height: 5px;
      width: 5px;
      border: 1px solid ${({ alt, altTheme }) =>
        alt || altTheme ? standardColours.white : brandColours.primary};
      border-top: none;
      border-left: none;
      border-radius: 1px;
      transform: translateY(-50%) rotate(45deg);
    `}

    ${minBreakpointQuery.xxlarge`
      height: 7px;
      width: 7px;
      border-width: 2px;
    `}
  }

  &.current-page {
    &:before {
      ${minBreakpointQuery.mlarge`
        opacity: 1;
      `}
    }
  }
`;

// moved up for hover
const StyledMegaMenu = styled(MegaMenu)`
  ${maxBreakpointQuery.mlarge`
    display: none;
  `}

  ${minBreakpointQuery.mlarge`
    left: -9999em;
  `}

  ${({ hasMobileDropdown }) => {
    if (!hasMobileDropdown) {
      return css`
        ${maxBreakpointQuery.mlarge`
          ${visuallyHidden()};
        `}
      `;
    }
  }}
`;

const StyledItem = styled.li`
  ${maxBreakpointQuery.mlarge`
    border-top: 1px solid ${standardColours.transparentWhite(0.1)};
  `}

  ${minBreakpointQuery.mlarge`
    display: inline-block;
    margin-right: 5px;
    margin-left: 5px;
  `}

  ${minBreakpointQuery.large`
    margin-right: 8px;
    margin-left: 8px;
  `}

  ${minBreakpointQuery.xxlarge`
    margin-right: 10px;
    margin-left: 10px;
  `}

  ${minBreakpointQuery.xxxlarge`
    margin-right: 12px;
    margin-left: 12px;
  `}

  ${minBreakpointQuery.xxxxlarge`
    margin-right: 14px;
    margin-left: 14px;
  `}

  &:hover {
    ${StyledLink} {
      &:before {
        ${minBreakpointQuery.mlarge`
          opacity: 1;
        `}
      }
    }

    ${StyledMegaMenu} {
      ${minBreakpointQuery.mlarge`
        right: 0;
        left: 0;
      `}

      ${minBreakpointQuery.large`
        right: 40px;
        left: 40px;
      `}

      ${minBreakpointQuery.xlarge`
        right: 80px;
        left: 80px;
      `}
    }

    ${({ hasMobileDropdown }) => {
      if (hasMobileDropdown) {
        return css`
          ${StyledLink} {
            &:after {
              ${maxBreakpointQuery.mlarge`
                transform: translateY(-50%) rotate(90deg);
              `}
            }
          }

          ${StyledMegaMenu} {
            ${maxBreakpointQuery.mlarge`
              display: block;
            `}
          }
        `;
      }
    }}
  }
`;

const Navigation = ({
  menus,
  alt,
  altTheme,
  isNavActive,
  setNavActive,
  ...props
}) => (
  <StyledNavigation {...props}>
    <StyledToggle
      aria-label="Open / Close Navigation"
      isNavActive={isNavActive}
      onClick={() => setNavActive(!isNavActive)}
    >
      <span />
      <span />
      <span />
    </StyledToggle>
    <StyledList isNavActive={isNavActive}>
      {menus.map(
        (
          {
            text,
            url,
            locations,
            regions,
            discoverMore,
            hotels,
            categories,
            insiderTopics,
            insiderSections,
            simpleLinks,
            banner,
          },
          i,
        ) => {
          const children = [];

          if (locations) {
            children.push({
              text: 'Popular Locations',
              children: locations,
            });
          }

          if (regions) {
            children.push({
              text: `${text} by Region`,
              children: regions,
            });
          }

          if (discoverMore) {
            children.push({
              text: 'Discover More',
              children: discoverMore,
            });
          }

          if (hotels) {
            children.push({
              text: 'Our Spa Groups & Hotels',
              children: hotels,
            });
          }

          if (categories) {
            children.push({
              text: 'Choose a Package Type',
              children: categories,
            });
          }

          if (insiderTopics) {
            children.push({
              text: 'Popular Topics',
              children: insiderTopics,
            });
          }

          if (insiderSections) {
            children.push({
              text: 'Spa Insider Sections',
              children: insiderSections,
            });
          }

          if (simpleLinks) {
            children.push({
              text: text,
              children: simpleLinks,
            });
          }

          return (
            <StyledItem
              key={`menu-item-${i}`}
              hasMobileDropdown={url === 'javascript:void(0)'}
            >
              <StyledLink
                {...(url !== 'javascript:void(0)'
                  ? {
                      to: url,
                      onClick: () =>
                        url === window.location.pathname && setNavActive(false),
                    }
                  : {
                      href: url,
                    })}
                alt={alt}
                altTheme={altTheme}
                activeClassName="current-page"
              >
                <span>{text}</span>
              </StyledLink>
              <StyledMegaMenu
                items={children}
                banner={banner}
                hasMobileDropdown={url === 'javascript:void(0)'}
                isVariant={true}
              />
            </StyledItem>
          );
        },
      )}
    </StyledList>
  </StyledNavigation>
);

export default Navigation;
